import { Injectable, inject } from '@angular/core';
import { AuthenticationDataService } from '../../data-services/authentication/authentication.service';
import type { ZodType } from 'zod';
import { AgentConfig } from '../models/agent-config.model';
import { AiConversation, AiConversationItem, AiConversationSummary, AiRequestOptions, AiTransport, TokenUsage, TurnMeta, TurnCorrelation, TurnCorrelationRecorder, TurnOutcome, TurnUsageRecorder } from './ai-transport';
import { unfenceJson } from './json-fence';
import { JsonSchemaFormat, jsonSchemaFormat } from './structured-output';
import { AiToolRegistryService } from '../tools/tool-registry.service';
import { buildForest, renderDeviceContext, EMPTY_FOREST } from '../grounding/grounding-forest';
import { renderSkillsJson } from './skills-json';
import { AiDataSourceRegistry } from '../summary/data-source-registry.service';
import { RepContextService } from '../services/rep-context.service';
import { AssistantFeedbackService } from '../services/assistant-feedback.service';
import { AiTelemetryService } from '../services/ai-telemetry.service';

/**
 * A fresh W3C traceparent (`00-<32-hex trace>-<16-hex span>-01`) for one agent turn. Foundry adopts
 * the trace id as the trace's `operation_Id`, so sending this lets the client key the rep's later
 * thumbs vote to the exact trace (see {@link TurnCorrelation}). The App Insights web SDK doesn't
 * inject correlation headers cross-origin, so we send our own.
 */
function newTraceparent(): { header: string; traceId: string; spanId: string } {
  const hex = (bytes: number): string => {
    const buf = new Uint8Array(bytes);
    crypto.getRandomValues(buf);
    let out = '';
    for (const b of buf) out += b.toString(16).padStart(2, '0');
    return out;
  };
  const traceId = hex(16);
  const spanId = hex(8);
  return { header: `00-${traceId}-${spanId}-01`, traceId, spanId };
}

/**
 * The OpenTelemetry `error.type` of a failed turn: the HTTP status code of an API error, else the error's
 * name when it is more specific than `Error`, else `_OTHER`, the convention's fallback value.
 */
function errorTypeOf(error: unknown): string {
  const { status, name } = (error ?? {}) as { status?: unknown; name?: unknown };
  if (typeof status === 'number') return String(status);
  if (typeof name === 'string' && name && name !== 'Error') return name;
  return '_OTHER';
}

/**
 * Minimal structural view of the `openai` client surface we use, so the
 * dynamically-imported SDK doesn't leak into our types or the bundle.
 */
type FoundryResponse = {
  /** The `resp_…` id of this specific call — the LAST one in a turn's tool loop is what
   *  feedback correlates against. */
  id?: string;
  output_text?: string;
  output?: Array<{
    type?: string;
    /** Function-call (and mcp-approval-request) items carry these. */
    id?: string;
    call_id?: string;
    name?: string;
    arguments?: string;
    content?: Array<{ text?: string }>;
  }>;
  /** Token accounting for this single call — a turn spanning tool hops sums these. */
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    input_tokens_details?: { cached_tokens?: number };
    output_tokens_details?: { reasoning_tokens?: number };
  };
  /** The model deployment that produced this response. */
  model?: string;
  /** The agent that produced this response; carries the version for feedback-annotation metadata. */
  agent_reference?: { name?: string; version?: string | number };
};
/** The per-request options of the `openai` SDK that a turn sets: its trace header and, optionally, its limits. */
interface FoundryRequestOptions {
  headers?: Record<string, string>;
  /** Milliseconds before the request fails with a timeout; the client default applies when unset. */
  timeout?: number;
  /** Resends of a failed request; the client default applies when unset. */
  maxRetries?: number;
  /** Aborts the request, including the read of its response body. */
  signal?: AbortSignal;
}

/** The SDK request options for the limits and abort signal in `options`; fields left unset are omitted, so the client defaults apply. */
function requestLimits(options?: AiRequestOptions): FoundryRequestOptions {
  return {
    ...(options?.timeoutMs !== undefined ? { timeout: options.timeoutMs } : {}),
    ...(options?.maxRetries !== undefined ? { maxRetries: options.maxRetries } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  };
}

type FoundryOpenAIClient = {
  conversations: {
    create(body: Record<string, unknown>, options?: FoundryRequestOptions): Promise<{ id: string }>;
    retrieve(id: string): Promise<{ id: string; metadata?: Record<string, string> | null }>;
    update(id: string, body: { metadata: Record<string, string> }): Promise<unknown>;
    delete(id: string): Promise<unknown>;
    items: {
      list(id: string, params?: Record<string, unknown>): Promise<{
        data: Array<{ id?: string; role?: string; content?: Array<{ text?: string }> }>;
        has_more?: boolean;
        last_id?: string;
      }>;
    };
  };
  responses: {
    create(body: Record<string, unknown>, options?: FoundryRequestOptions): Promise<FoundryResponse>;
  };
  get<T>(path: string, options?: { query?: Record<string, unknown> }): Promise<T>;
};

/** One page of the agent endpoint's conversation list, most recently created first. */
interface FoundryConversationPage {
  data?: Array<{ id: string; created_at: number; metadata?: Record<string, string> | null }>;
  has_more?: boolean;
  last_id?: string;
}

/** Largest page the agent endpoint's conversation list returns. */
const CONVERSATION_PAGE_SIZE = 100;

/** Conversation metadata key that marks a conversation deleted from the user's history, holding the time of the delete. */
const DELETED_AT = 'deletedAt';

/** The summary of one conversation from the agent endpoint's conversation list. */
function summaryOf(conversation: NonNullable<FoundryConversationPage['data']>[number]): AiConversationSummary {
  const metadata = conversation.metadata ?? {};
  return {
    id: conversation.id,
    createdAt: new Date(conversation.created_at * 1000).toISOString(),
    metadata,
    deleted: DELETED_AT in metadata,
  };
}

/** Maximum tool-call hops per user turn (prevents runaway loops). */
const MAX_TOOL_HOPS = 5;

/**
 * Calls a Microsoft Foundry hosted prompt agent via the OpenAI-compatible Responses API, over a
 * host-side conversation for multi-turn context (buildClient sets up the agent- vs model-mode
 * wire). Client-side `@AITool` calls run in a loop up to MAX_TOOL_HOPS; the tool catalogue lives
 * on the agent definition, not on per-request bodies.
 */
@Injectable({ providedIn: 'root' })
export class FoundryTransport implements AiTransport {
  /** Entra resource for Foundry; `getTokenForResource` appends `/.default`. */
  private static readonly SCOPE = 'https://ai.azure.com';

  private readonly toolRegistry = inject(AiToolRegistryService);
  private readonly repContext = inject(RepContextService);
  private readonly sources = inject(AiDataSourceRegistry);
  private readonly feedback = inject(AssistantFeedbackService);
  private readonly telemetry = inject(AiTelemetryService);

  constructor(private readonly auth: AuthenticationDataService) {}

  /**
   * The rep's session/identity context + the agent routing key, as a closure resolved fresh per
   * request (keeps the Dataverse token current). The transport attaches this to every agent
   * call so rep context is uniform by construction — features never assemble it.
   */
  #userContext(agent: AgentConfig): () => Promise<Record<string, unknown>> {
    return async () => ({ ...await this.repContext.buildUserContext(), category: String(agent.category) });
  }

  /**
   * The agent's grounding forest, resolved fresh per request from its config + the in-scope record
   * (configured device sources → sections → forest). Undefined when the agent has no grounding
   * config — agent-mode calls then send the EMPTY_FOREST (the resolver's absence means "nothing to
   * ground", not "omit the field"; the field is always present and always parseable).
   */
  #groundingResolver(agent: AgentConfig): ((recordId?: string) => Promise<string>) | undefined {
    const config = agent.groundingConfig;
    if (!config) return undefined;
    return async recordId => {
      const sections = await this.sources.resolve(agent.category, { recordId });
      // Always a valid forest: the config's server sources survive even when the record resolves no
      // device data, so the grounding MCP always receives parseable JSON (never null).
      return buildForest(config, sections ?? []);
    };
  }

  async startConversation(agent: AgentConfig, metadata?: Record<string, string>, options?: AiRequestOptions): Promise<AiConversation> {
    const client = await this.buildClient(agent);
    const conversation = await client.conversations.create(metadata ? { metadata } : {}, requestLimits(options));
    // New conversation → seed the agent's additional instructions on the first turn (applies to ALL agents).
    return new FoundryConversation(client, conversation.id, this.toolRegistry, agent, true, this.#userContext(agent), this.#groundingResolver(agent), c => this.feedback.rememberTurn(c), t => void this.telemetry.trackTurn(agent.endpoint, t));
  }

  async resumeConversation(agent: AgentConfig, conversationId: string): Promise<AiConversation> {
    const client = await this.buildClient(agent);
    // Resumed conversation already carries its seeded instructions — don't re-add.
    return new FoundryConversation(client, conversationId, this.toolRegistry, agent, false, this.#userContext(agent), this.#groundingResolver(agent), c => this.feedback.rememberTurn(c), t => void this.telemetry.trackTurn(agent.endpoint, t));
  }

  /** Marks the conversation deleted by adding {@link DELETED_AT} to its metadata. A metadata update replaces the
   *  whole set, so the conversation's existing keys are sent with the mark. */
  async deleteConversation(agent: AgentConfig, conversationId: string): Promise<void> {
    const client = await this.buildClient(agent);
    const { metadata } = await client.conversations.retrieve(conversationId);
    await client.conversations.update(conversationId, {
      metadata: { ...metadata, [DELETED_AT]: new Date().toISOString() },
    });
  }

  /** Reads the agent endpoint's conversation list, which the SDK has no method for, through the client's own request
   *  path, so the rep's token, the preview headers and the retries are those of every other call. Pages are read
   *  most recently created first until `enough` holds or the list ends. */
  async listConversations(
    agent: AgentConfig,
    enough: (read: AiConversationSummary[]) => boolean = () => false,
  ): Promise<AiConversationSummary[]> {
    const client = await this.buildClient(agent);
    const conversations: AiConversationSummary[] = [];
    let after: string | undefined;
    do {
      const query = { limit: CONVERSATION_PAGE_SIZE, order: 'desc', ...(after ? { after } : {}) };
      const page = await client.get<FoundryConversationPage>('/conversations', { query });
      conversations.push(...(page.data ?? []).map(summaryOf));
      after = page.has_more && !enough(conversations) ? page.last_id : undefined;
    } while (after);
    return conversations;
  }

  private async buildClient(agent: AgentConfig): Promise<FoundryOpenAIClient> {
    const { default: OpenAI } = await import('openai');
    const apiKey = async () => {
      const token = await this.auth.getTokenForResource(FoundryTransport.SCOPE);
      if (!token?.token) {
        throw new Error(`Failed to acquire token for scope ${FoundryTransport.SCOPE}`);
      }
      return token.token;
    };

    // Direct-model mode: call the project's model endpoint with no agent reference.
    // The path is version-pinned (/openai/v1), so no api-version query and no
    // agent-only preview headers — those belong to the agent protocol surface.
    let openaiParams: ConstructorParameters<typeof OpenAI>[0] = {
      baseURL: `${agent.endpoint}/openai/v1`,
      apiKey,
      dangerouslyAllowBrowser: true,
      maxRetries: 2,
    };
    if (!agent.model) {
      openaiParams = {
        ...openaiParams,
        defaultQuery: { 'api-version': 'v1' },
        defaultHeaders: {
          'Foundry-Features': 'HostedAgents=V1Preview,AgentEndpoints=V1Preview',
        },
        baseURL: `${agent.endpoint}/agents/${agent.name}/endpoint/protocols/openai`,
      };
    }
    return new OpenAI(openaiParams) as FoundryOpenAIClient;
  }
}

class FoundryConversation implements AiConversation {
  #seedPending: boolean;
  #structuredInputs: Record<string, unknown> | undefined;
  #requestTools: ReturnType<AiToolRegistryService['toFoundryToolDefinitionsFor']> | undefined;
  #responseFormat: JsonSchemaFormat | undefined;
  /** Feedback-correlation metadata for the turn most recently completed by `send()`/`parse()`. */
  #lastTurn: TurnMeta | null = null;

  /** See {@link AiConversation.lastTurn}. */
  get lastTurn(): TurnMeta | null {
    return this.#lastTurn;
  }

  constructor(
    private readonly client: FoundryOpenAIClient,
    readonly id: string,
    private readonly toolRegistry: AiToolRegistryService,
    private readonly agent: AgentConfig,
    seedSystem = false,
    private readonly sessionContext?: () => Promise<Record<string, unknown>>,
    private readonly resolveGrounding?: (recordId?: string) => Promise<string | null>,
    private readonly recordCorrelation?: TurnCorrelationRecorder,
    private readonly recordUsage?: TurnUsageRecorder,
  ) {
    this.#seedPending = seedSystem && !!agent.additionalInstructions;
  }

  /** Set structured inputs to send on the NEXT request (e.g. summary `context`). */
  setStructuredInputs(inputs: Record<string, unknown>): void {
    this.#structuredInputs = inputs;
  }

  /**
   * Expose a named subset of client `@AITool` tools on every request of this
   * conversation. Direct-model mode only: the tool definitions must ride in the
   * request body there, whereas hosted agents carry their catalogue on the
   * agent definition (and the agent endpoint owns the `tools` key).
   */
  setRequestTools(toolNames: string[]): void {
    if (!this.agent.model) {
      console.warn('[FoundryConversation] setRequestTools ignored: hosted-agent conversations take tools from the agent definition');
      return;
    }
    this.#requestTools = this.toolRegistry.toFoundryToolDefinitionsFor(toolNames);
  }

  /** See {@link AiConversation.setResponseFormat}. Rides on every request of the conversation. */
  setResponseFormat(schemaName: string, schema: ZodType): void {
    if (!this.agent.model) {
      console.warn('[FoundryConversation] setResponseFormat ignored: hosted-agent conversations take their output format from the agent definition');
      return;
    }
    this.#responseFormat = jsonSchemaFormat(schemaName, schema);
  }

  /**
   * The structured inputs for a turn: the rep's session context (base) overlaid with the feature's
   * inputs, plus grounding delivered for this transport mode. Agent mode always carries
   * `groundingConfig` (the forest when grounded, else the EMPTY_FOREST) — it's part of the uniform
   * agent contract, so the field is never conditionally absent and is always parseable JSON; model
   * mode renders the forest's device nodes into `context` (no MCP to assemble it).
   */
  /**
   * The identity + routing that stay constant for the whole turn: the rep's session context, the
   * feature's structured inputs, the ids the grounding MCP resolves against — `agentConfigId`
   * (the config row this agent was resolved from) and `dataverseMcpServerId` (a config's specific
   * Dataverse MCP) — and `skills`, the JSON catalogue of the skills bound to this config.
   * These ride on EVERY request of a turn, including the tool-loop follow-ups, so server-side
   * skill resolution and rep impersonation survive a client-tool round-trip: a follow-up that
   * reached the MCP without them silently resolved zero skills.
   */
  async #persistentInputs(): Promise<Record<string, unknown> | undefined> {
    let inputs = this.sessionContext
      ? { ...(await this.sessionContext()), ...(this.#structuredInputs ?? {}) }
      : this.#structuredInputs;

    if (this.agent.id) {
      inputs = { ...(inputs ?? {}), agentConfigId: this.agent.id };
    }
    if (this.agent.dataverseMcpServerId) {
      inputs = { ...(inputs ?? {}), dataverseMcpServerId: this.agent.dataverseMcpServerId };
    }
    const skills = renderSkillsJson(this.agent.skills);
    if (skills) {
      inputs = { ...(inputs ?? {}), skills };
    }
    return inputs;
  }

  /**
   * A turn's full inputs: the persistent identity/routing (from {@link #persistentInputs}) plus this
   * user turn's `userQuery` and resolved grounding. Those last two belong to the turn only and are
   * NOT re-sent on tool follow-ups.
   */
  async #requestInputs(persistent: Record<string, unknown> | undefined, userQuery?: string): Promise<Record<string, unknown> | undefined> {
    let inputs = persistent;
    if (userQuery) {
      inputs = { ...(inputs ?? {}), userQuery };
    }

    const forest = this.resolveGrounding
      ? await this.resolveGrounding(inputs?.['recordId'] as string | undefined)
      : null;

    if (this.agent.model) {
      // Model mode: render the forest's device nodes into `context` (no MCP to assemble it).
      if (forest) {
        const context = renderDeviceContext(forest);
        if (context !== undefined) inputs = { ...inputs, context };
      }
    } else {
      // Agent mode: groundingConfig is part of the uniform agent contract — the forest when
      // grounded, else the EMPTY_FOREST — so the agent's declared input is satisfied and the
      // grounding MCP always gets parseable JSON, never raw null. Per-turn: not re-sent on follow-ups.
      inputs = { ...inputs, groundingConfig: forest ?? EMPTY_FOREST };
    }
    return inputs;
  }

  /**
   * Wrap a turn's structured inputs into the Responses API body fields, by transport mode.
   *
   * Direct-model (`agent.model` set): top-level `model` + `instructions` (the endpoint substitutes
   * `{{key}}` handlebar placeholders from `structured_inputs` before the model runs).
   *
   * Agent mode (no model): the agent endpoint rejects a top-level `instructions`, so the agent's
   * instructions ride inside `structured_inputs` (its prompt reads `{{instructions}}`).
   */
  #bodyExtras(inputs?: Record<string, unknown>): Record<string, unknown> {
    if (this.agent.model) {
      const extras: Record<string, unknown> = { model: this.agent.model };
      const instructions = this.#withContextPlaceholder(this.agent.instructions, !!inputs?.['context']);
      if (instructions) extras['instructions'] = instructions;
      if (inputs) extras['structured_inputs'] = inputs;
      // Tool-loop follow-ups must re-declare the tools or the API rejects the
      // function_call_output items, so these ride on every request while set.
      if (this.#requestTools?.length) extras['tools'] = this.#requestTools;
      if (this.#responseFormat) extras['text'] = { format: this.#responseFormat };
      return extras;
    }
    const withInstructions = this.agent.instructions
      ? { ...inputs, instructions: this.agent.instructions }
      : inputs;
    return withInstructions ? { structured_inputs: withInstructions } : {};
  }

  /**
   * Ensure the model-mode instructions reference `{{context}}` so the resolved context is
   * templated in even when the config author omitted the placeholder. The context already
   * carries its own section labels, so nothing is prepended. No-op when there's no context.
   */
  #withContextPlaceholder(instructions: string | undefined, hasContext: boolean): string | undefined {
    if (!hasContext) return instructions;
    const base = instructions ?? '';
    if (base.includes('{{context}}')) return base;
    return base ? `${base}\n\n{{context}}` : '{{context}}';
  }

  /**
   * Sends one turn and runs the client tool loop, at most {@link MAX_TOOL_HOPS} follow-ups.
   *
   * - The rep context, `agentConfigId` and skills ride on every request of the turn, tool follow-ups
   *   included, so the grounding MCP can resolve skills and impersonate the rep on each hop; the rep's
   *   `userQuery` and the resolved grounding ride on the first request only.
   * - The feature's one-shot structured inputs are cleared after the first request, so a later turn on a
   *   reused conversation reads fresh ones.
   * - Every request carries one W3C traceparent; Foundry adopts its trace id as the trace's operation id,
   *   which a later feedback vote and the turn's `invoke_agent` span use.
   * - MCP approval requests are denied; the agent's instructions direct it to the client tool catalogue.
   * - `options` sets the timeout, the resend count and the abort signal of every request of the turn;
   *   unset fields keep the client's defaults.
   * - Token usage is summed across every hop. The final response's ids are what feedback correlates against.
   * - The turn is recorded once, as `success` with the reply text, or as `failure` with the usage of the
   *   hops that returned and the error's type when any step throws; the error is then rethrown.
   */
  async send(payload: unknown, options?: AiRequestOptions): Promise<string> {
    const startedAt = performance.now();
    const startTime = Date.now();
    const trace = newTraceparent();
    const requestOptions: FoundryRequestOptions = {
      headers: { traceparent: trace.header },
      ...requestLimits(options),
    };
    const question = this.#extractUserText(payload);
    const origin = originOf(this.#structuredInputs);
    const usage: TokenUsage = { inputTokens: 0, outputTokens: 0, totalTokens: 0, cachedInputTokens: 0, reasoningTokens: 0 };
    let lastResponse: FoundryResponse | undefined;
    let response: FoundryResponse;
    try {
      const persistent = await this.#persistentInputs();
      const inputs = await this.#requestInputs(persistent, question);
      response = lastResponse = await this.client.responses.create({
        conversation: this.id,
        input: this.#withSystem(this.#shapeInput(payload)),
        ...this.#bodyExtras(inputs),
      }, requestOptions);
      this.#structuredInputs = undefined;
      this.#accumulateUsage(usage, response);

      for (let hop = 0; hop < MAX_TOOL_HOPS; hop++) {
        const calls = this.#extractToolCalls(response);
        const approvalIds = this.#extractMcpApprovalRequestIds(response);
        if (calls.length === 0 && approvalIds.length === 0) break;

        const outputs: Array<Record<string, unknown>> = [];
        for (const c of calls) {
          outputs.push({
            type: 'function_call_output',
            call_id: c.call_id,
            output: this.#serializeToolResult(await this.toolRegistry.execute(c.name, c.arguments)),
          });
        }
        for (const id of approvalIds) {
          outputs.push({
            type: 'mcp_approval_response',
            approval_request_id: id,
            approve: false,
          });
        }

        response = lastResponse = await this.client.responses.create({
          conversation: this.id,
          input: outputs,
          ...this.#bodyExtras(persistent),
        }, requestOptions);
        this.#accumulateUsage(usage, response);
      }
    } catch (e) {
      this.#recordTurn({ trace, startedAt, startTime, usage, response: lastResponse, outcome: 'failure', errorType: errorTypeOf(e), question, origin });
      throw e;
    }

    this.#lastTurn = {
      itemId: response.output?.find(i => i.type === 'message')?.id,
      responseId: response.id,
      usage,
    };
    this.#recordTurnCorrelation(response, trace, usage);
    const answer = this.#replyText(response);
    this.#recordTurn({ trace, startedAt, startTime, usage, response, outcome: 'success', question, answer, origin });

    return answer;
  }

  /**
   * Hands one finished turn to the usage recorder. `response` is the last response received, if any;
   * the duration runs from `startedAt` (a `performance.now()` reading) to this call, and `startTime` is
   * the same instant on the wall clock.
   */
  #recordTurn(turn: {
    trace: { traceId: string; spanId: string };
    startedAt: number;
    startTime: number;
    usage: TokenUsage;
    response?: FoundryResponse;
    outcome: TurnOutcome;
    errorType?: string;
    question?: string;
    answer?: string;
    origin?: string;
  }): void {
    const { response } = turn;
    const identity = response ? this.#agentIdentity(response) : { agentName: this.agent.name };
    this.recordUsage?.({
      conversationId: this.id,
      responseId: response?.id,
      traceId: turn.trace.traceId,
      parentSpanId: turn.trace.spanId,
      ...identity,
      origin: turn.origin,
      model: response?.model ?? this.agent.model,
      usage: turn.usage,
      startTime: turn.startTime,
      durationMs: performance.now() - turn.startedAt,
      outcome: turn.outcome,
      errorType: turn.errorType,
      question: turn.question,
      answer: turn.answer,
    });
  }

  /**
   * The assistant's reply text in `response`: the aggregated `output_text`, else the text of its
   * `message` items. Tool calls, tool results and reasoning items are never included.
   */
  #replyText(response: FoundryResponse): string {
    if (response.output_text !== undefined) return response.output_text;
    return (response.output ?? [])
      .filter(item => item.type === 'message')
      .flatMap(item => item.content ?? [])
      .map(content => content.text ?? '')
      .join('');
  }

  #recordTurnCorrelation(response: FoundryResponse, trace: { traceId: string; spanId: string }, usage: TokenUsage): void {
    const itemId = response.output?.find(i => i.type === 'message')?.id;
    if (!this.recordCorrelation || !response.id || !itemId) return;
    const { agentName, agentVersion } = this.#agentIdentity(response);
    this.recordCorrelation({
      itemId,
      conversationId: this.id,
      responseId: response.id,
      traceId: trace.traceId,
      agentName,
      agentVersion,
      agentId: agentVersion ? `${agentName}:${agentVersion}` : agentName,
      usage,
      endpoint: this.agent.endpoint,
    });
  }

  /** The agent that answered: the response's agent reference, else this conversation's configured agent. */
  #agentIdentity(response: FoundryResponse): { agentName: string; agentVersion?: string } {
    const rawVersion = response.agent_reference?.version;
    return {
      agentName: response.agent_reference?.name ?? this.agent.name,
      agentVersion: rawVersion !== undefined && rawVersion !== null ? String(rawVersion) : undefined,
    };
  }

  async parse<TResult>(payload: unknown, _schema: unknown, _schemaName: string): Promise<TResult> {
    // The agent endpoint rejects per-request `text.format`; structured-output enforcement lives on
    // the agent definition (strict:true), so the model already returns valid JSON. Route through
    // `send` so the tool loop runs first (agents with both tools and structured outputs emit the
    // final JSON only after tools execute), then parse. `send` sets `#lastTurn`, so callers of
    // `parse` get the same feedback-correlation metadata for free.
    const text = await this.send(payload);
    if (!text) return undefined as unknown as TResult;
    const unfenced = unfenceJson(text);
    try {
      return JSON.parse(unfenced) as TResult;
    } catch (e) {
      console.warn('Foundry parse: text was not JSON; returning undefined.', e);
      return undefined as unknown as TResult;
    }
  }

  async listItems(): Promise<AiConversationItem[]> {
    // Ascending = chronological, so callers (history rendering) get oldest-first. The server
    // pages items (20 by default) — follow the cursor to the end, or a long conversation's
    // newest turns silently fall off the restore.
    const out: AiConversationItem[] = [];
    let after: string | undefined;
    do {
      const page = await this.client.conversations.items.list(this.id, {
        order: 'asc',
        limit: 100,
        ...(after ? { after } : {}),
      });
      for (const item of page.data ?? []) {
        out.push({ id: item.id ?? '', role: item.role ?? '', text: item.content?.[0]?.text ?? '' });
      }
      after = page.has_more ? page.last_id : undefined;
    } while (after);
    return out;
  }

  async close(): Promise<void> {
    // Non-destructive: drop the local handle only. The server-side conversation must
    // survive so the rep can resume it from history.
  }

  async delete(): Promise<void> {
    try {
      await this.client.conversations.delete(this.id);
    } catch {
      // Best-effort: host-side conversations also expire on their own.
    }
  }


  /**
   * Returns only the function calls needing client dispatch: `function_call` items NOT already
   * paired with a `function_call_output` in the same response. Foundry's server-side built-in tools
   * return both halves pre-paired — dispatching those against the registry would post an "unknown
   * tool" error back and confuse the next turn. Our client `@AITool` calls have no output until we add one.
   */
  #extractToolCalls(response: FoundryResponse): Array<{ call_id: string; name: string; arguments: string }> {
    const out = response.output ?? [];
    const resolvedCallIds = new Set<string>();
    for (const item of out) {
      if (item?.type === 'function_call_output' && item.call_id) {
        resolvedCallIds.add(item.call_id);
      }
    }
    const calls: Array<{ call_id: string; name: string; arguments: string }> = [];
    for (const item of out) {
      if (item?.type === 'function_call' && item.call_id && item.name && !resolvedCallIds.has(item.call_id)) {
        calls.push({ call_id: item.call_id, name: item.name, arguments: item.arguments ?? '' });
      }
    }
    return calls;
  }

  /**
   * Returns the `approval_request_id`s for any `mcp_approval_request` output
   * items. Foundry emits these whenever an MCP server with
   * `require_approval: "always"` would fire a tool — the agent's run halts
   * until we post back `mcp_approval_response` items with matching ids.
   */
  #extractMcpApprovalRequestIds(response: FoundryResponse): string[] {
    const out = response.output ?? [];
    const ids: string[] = [];
    for (const item of out) {
      if (item?.type === 'mcp_approval_request' && item.id) ids.push(item.id);
    }
    return ids;
  }

  /** Adds one call's `usage` onto the running turn total, in place. Missing fields count as 0. */
  #accumulateUsage(total: TokenUsage, response: FoundryResponse): void {
    total.inputTokens += response.usage?.input_tokens ?? 0;
    total.outputTokens += response.usage?.output_tokens ?? 0;
    total.totalTokens += response.usage?.total_tokens ?? 0;
    total.cachedInputTokens = (total.cachedInputTokens ?? 0) + (response.usage?.input_tokens_details?.cached_tokens ?? 0);
    total.reasoningTokens = (total.reasoningTokens ?? 0) + (response.usage?.output_tokens_details?.reasoning_tokens ?? 0);
  }

  #serializeToolResult(result: unknown): string {
    if (typeof result === 'string') return result;
    try {
      return JSON.stringify(result);
    } catch {
      return String(result);
    }
  }

  /**
   * The rep's raw query text for a turn: a string payload as-is, or the LAST user
   * message in a role-tagged input array (features send arrays on turns that also
   * carry developer/system context). Non-user payload shapes have no query.
   */
  #extractUserText(payload: unknown): string | undefined {
    if (typeof payload === 'string') return payload;
    if (Array.isArray(payload)) {
      for (let i = payload.length - 1; i >= 0; i--) {
        const item = payload[i] as { role?: string; content?: unknown } | undefined;
        if (item?.role === 'user' && typeof item.content === 'string') return item.content;
      }
    }
    return undefined;
  }

  /**
   * Normalise the feature's payload into the body field the Responses API expects:
   *  - string → use as-is (most chat turns).
   *  - array  → pass through (role-tagged message items).
   *  - object → JSON.stringify.
   */
  #shapeInput(payload: unknown): unknown {
    if (typeof payload === 'string') return payload;
    if (Array.isArray(payload)) return payload;
    return JSON.stringify(payload);
  }

  /**
   * First turn of a NEW conversation only: prepend the agent's additional instructions as a
   * `system` message so they apply to EVERY agent uniformly (record summary, meeting, assistant).
   * Resumed conversations already carry the seeded instructions, so this is a no-op there.
   */
  #withSystem(base: unknown): unknown {
    if (!this.#seedPending || !this.agent.additionalInstructions) return base;
    this.#seedPending = false;
    const sys = { type: 'message', role: 'system', content: this.agent.additionalInstructions };
    if (Array.isArray(base)) return [sys, ...base];
    if (typeof base === 'string') return [sys, { type: 'message', role: 'user', content: base }];
    return [sys, { type: 'message', role: 'user', content: JSON.stringify(base) }];
  }
}

/** The `origin` structured input of a request: the app surface that asked, or undefined when none is named. */
function originOf(inputs: Record<string, unknown> | undefined): string | undefined {
  const origin = inputs?.['origin'];
  return typeof origin === 'string' && origin.trim() ? origin.trim() : undefined;
}
